import {
  Fragment, useEffect, useId, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useRemoteSearch } from "../utils/useRemoteSearch";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Optional leading node (flag, icon, color dot). */
  left?: ReactNode;
  /** Optional heading this option sits under. Consecutive options sharing one are grouped. */
  group?: string;
  /** Extra text the search matches on but the row does not show — e.g. a VAT number or code. */
  keywords?: string;
};

type Props<T extends string> = {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /**
   * Show the search box once the list is at least this long. Default 10 — below that
   * the whole list is on screen and a search box is one more thing to look past.
   * Pass 0 to always search, or Infinity to never.
   */
  searchThreshold?: number;
  /**
   * Reach beyond the options already loaded: the search box sends the query here and the returned
   * options are appended to whatever matched locally.
   *
   * <p>Pass this whenever `options` is a PAGE of a register rather than the whole of it. Four
   * dropdowns were built on `listCompanies(0, 200)` out of 7,902 clients — invite, contracts,
   * forecasts, the orders filter — so 7,702 companies simply had no option to pick, and the search
   * box made that look like "no such company" (2026-10-05).
   *
   * <p>Local matching still runs, so static options the caller owns (an "internal" entry, an "all"
   * entry) keep working and are never replaced by the server's answer.
   */
  onSearch?: (query: string) => Promise<SelectOption<T>[]>;
  /** Applied to the wrapper — use for width / margins. */
  style?: CSSProperties;
};

/**
 * Strips diacritics and case so "Aljancic" finds "Aljančič" and "zaletelj" finds "Jože
 * Zaletelj". Most of this app's option lists are Slovenian and Serbian names; a plain
 * lowercase compare makes the search box look broken to anyone typing on a keyboard
 * without those letters, which is most people using it.
 */
function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * App-styled replacement for native <select>. The closed control looks like an
 * `.input`; the open list is a custom popover (var(--surface)/var(--border)) so
 * it matches the rest of the UI instead of the OS dropdown. Keyboard + outside
 * click supported, and lists past {@link Props.searchThreshold} options get a
 * search box. Use this for every fixed-option dropdown going forward.
 */
export function Select<T extends string>({
  value, options, onChange, disabled, placeholder, ariaLabel, searchThreshold = 10, style, onSearch,
}: Props<T>) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  // A server-backed list is always searchable: its length says nothing about the register behind it.
  const searchable = !!onSearch || options.length >= searchThreshold;
  const { results: remote } = useRemoteSearch(query, onSearch);
  // Everything below indexes `shown`, never `options` — with a query applied the two
  // differ, and arrowing through the original array would select an invisible row.
  const shown = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return options;
    const local = options.filter((o) => fold(o.label).includes(q)
      || (o.keywords ? fold(o.keywords).includes(q) : false));
    if (!remote) return local;
    // Local first (the caller's own entries lead), then whatever the server added. Deduped by
    // value: a company in both halves must not appear twice.
    const seen = new Set(local.map((o) => o.value));
    return [...local, ...remote.filter((o) => !seen.has(o.value))];
  }, [options, query, remote]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Highlight the current value each time the list opens, and start from a clean query —
  // a leftover filter would reopen the list showing a fraction of it with no clue why.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
    if (searchable) searchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing narrows the list under the cursor; keep the highlight on a row that exists.
  useEffect(() => { setActive(0); }, [query]);

  // Keep the highlighted row visible — without this, arrowing down a 40-name list moves
  // a highlight nobody can see.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open, active, query]);

  function choose(i: number) {
    const opt = shown[i];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (open) choose(active); else setOpen(true);
        break;
      case " ":
        // Only the closed trigger opens on Space; inside the search box it is a character.
        if (open && searchable) break;
        e.preventDefault();
        if (open) choose(active); else setOpen(true);
        break;
      case "Escape":
        setOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!open) { setOpen(true); break; }
        setActive((a) => Math.min(shown.length - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) { setOpen(true); break; }
        setActive((a) => Math.max(0, a - 1));
        break;
    }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        onKeyDown={onKeyDown}
        style={trigger}
      >
        <span style={{ ...triggerLabel, color: selected ? "var(--ink)" : "var(--ink-3)" }}>
          {selected?.left}
          {selected ? selected.label : (placeholder ?? "Select…")}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden
          style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s", color: "var(--ink-3)" }}>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div style={panel}>
          {searchable && (
            <input
              ref={searchRef}
              className="input"
              type="text"
              role="combobox"
              aria-expanded={true}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label={t("select.search.placeholder", "Search…")}
              placeholder={t("select.search.placeholder", "Search…")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              style={search}
            />
          )}
          <div ref={listRef} id={listId} role="listbox" className="dd-scroll" style={list}>
            {shown.length === 0 ? (
              <div style={empty}>{t("select.search.noMatches", "No matches")}</div>
            ) : shown.map((o, i) => {
              const isSel = o.value === value;
              // A heading whenever the group changes — the list keeps ONE flat option array, so
              // keyboard navigation still indexes options and never lands on a heading.
              const heading = o.group && o.group !== shown[i - 1]?.group
                ? <div key={`h-${o.group}`} style={groupHead} aria-hidden>{o.group}</div>
                : null;
              return (
                <Fragment key={o.value}>
                {heading}
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  data-active={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(i)}
                  style={{ ...option, background: i === active ? "var(--surface-2)" : "transparent", fontWeight: isSel ? 600 : 400 }}
                >
                  {o.left}
                  <span style={{ flex: 1 }}>{o.label}</span>
                  {isSel && (
                    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden style={{ flex: "none", color: "var(--imp-primary)" }}>
                      <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
                    </svg>
                  )}
                </button>
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const groupHead: CSSProperties = {
  padding: "8px 10px 4px", fontSize: 11, fontWeight: 600, letterSpacing: ".04em",
  textTransform: "uppercase", color: "var(--ink-3)",
};
const trigger: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, textAlign: "left", cursor: "pointer",
};
const triggerLabel: CSSProperties = {
  flex: 1, display: "flex", alignItems: "center", gap: 8,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const panel: CSSProperties = {
  position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, right: 0,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)",
  padding: 4, display: "flex", flexDirection: "column", gap: 4,
};
// The search box stays put while the options scroll under it: a filter you have to
// scroll back up to reach is worse than no filter on a long list.
const search: CSSProperties = { flex: "none", fontSize: 13 };
const list: CSSProperties = { maxHeight: 288, overflowY: "auto" };
const empty: CSSProperties = { padding: "10px", fontSize: 13, color: "var(--ink-3)" };
const option: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left",
  padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  fontSize: 13, color: "var(--ink)",
};
